import React, { useEffect, useRef, useState } from "react";
import { Film, X, Download, Loader2, Sparkles } from "lucide-react";
import { resolveAudio } from "@/lib/api";

/** Client-side TikTok/Reels clip maker.
 *  Renders a 720×1280 (9:16) portrait MP4/WebM by drawing a Canvas frame-by-frame
 *  (cover art + animated waveform + station name) and using MediaRecorder to encode it
 *  alongside the audio. Zero server cost — runs entirely in the broadcaster's browser. */
export default function ClipMaker({ station, audioUrl, audioDuration, title, artist, open, onClose, trackCoverUrl }) {
  const canvasRef = useRef(null);
  const audioElRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | recording | done | error
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const clipSeconds = Math.min(Math.max(Math.round(audioDuration || 20), 10), 30); // 10-30s

  useEffect(() => {
    if (!open) {
      setStatus("idle"); setProgress(0); setDownloadUrl(""); setErrMsg("");
    }
  }, [open]);

  const start = async () => {
    setStatus("recording"); setProgress(0); setErrMsg("");
    try {
      const canvas = canvasRef.current;
      canvas.width = 720; canvas.height = 1280;
      const ctx = canvas.getContext("2d");

      // Preload two images:
      //  - `trackCover`: per-song artwork (centered hero on the clip)
      //  - `stationCover`: station logo (small top-right badge for brand recall)
      // If trackCover is missing we fall back to stationCover for the hero too,
      // so the clip still looks good even without per-song art.
      const loadImg = (src) => new Promise((res) => {
        if (!src) return res(null);
        const i = new Image();
        i.crossOrigin = "anonymous";
        const u = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
        i.onload = () => res(i);
        i.onerror = () => res(null);
        i.src = u;
      });
      const [stationCover, trackCover] = await Promise.all([
        loadImg(station.cover_url),
        loadImg(trackCoverUrl),
      ]);
      const heroCover = trackCover || stationCover;          // centered big image
      const bgCover = trackCover || stationCover;            // blurred background
      const stationBadge = stationCover;                     // small brand badge

      // Audio + WebAudio analyser for the waveform
      const audio = audioElRef.current;
      audio.crossOrigin = "anonymous";
      audio.src = resolveAudio(audioUrl);
      audio.preload = "auto";
      audio.muted = false;
      audio.volume = 1.0;
      await audio.play().catch(() => {});
      audio.pause();
      audio.currentTime = 0;

      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      const src = ac.createMediaElementSource(audio);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 128;
      const dest = ac.createMediaStreamDestination();
      src.connect(analyser);
      src.connect(dest);  // route to recorder
      src.connect(ac.destination); // so user hears it while recording (optional)

      // Combine video (canvas) + audio (dest)
      const canvasStream = canvas.captureStream(30);
      const audioTrack = dest.stream.getAudioTracks()[0];
      const combined = new MediaStream([
        canvasStream.getVideoTracks()[0],
        audioTrack,
      ]);

      // Pick the best MIME the browser supports
      const candidates = ["video/mp4;codecs=avc1,mp4a.40.2", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
      const mime = candidates.find(m => window.MediaRecorder?.isTypeSupported?.(m)) || "video/webm";
      const recorder = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 3_000_000 });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        try { ac.close(); } catch (_) {}
        const blob = new Blob(chunks, { type: mime });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        setStatus("done");
      };

      // Animation loop
      const startTs = performance.now();
      const freqData = new Uint8Array(analyser.frequencyBinCount);
      const drawFrame = () => {
        const elapsed = (performance.now() - startTs) / 1000;
        setProgress(Math.min(1, elapsed / clipSeconds));

        // Background — blurred cover (track if available, station otherwise)
        ctx.fillStyle = "#050505";
        ctx.fillRect(0, 0, 720, 1280);
        if (bgCover && bgCover.complete && bgCover.naturalWidth > 0) {
          ctx.save();
          ctx.filter = "blur(40px) brightness(0.45)";
          ctx.drawImage(bgCover, -40, -40, 800, 1360);
          ctx.restore();
        }
        // Top-left brand
        ctx.fillStyle = "#F59E0B";
        ctx.font = "bold 28px Inter, system-ui, sans-serif";
        ctx.fillText("FLIBOE", 48, 64);
        ctx.fillStyle = "#94A3B8";
        ctx.font = "16px monospace";
        ctx.fillText("/ ON AIR  WORLDWIDE", 48, 92);

        // LIVE pill top-right
        ctx.fillStyle = "rgba(239,68,68,0.18)";
        ctx.fillRect(540, 38, 132, 38);
        ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 1.5; ctx.strokeRect(540, 38, 132, 38);
        ctx.fillStyle = "#EF4444";
        ctx.beginPath(); ctx.arc(560, 57, 5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#FECACA"; ctx.font = "bold 14px monospace"; ctx.fillText("LIVE", 580, 62);

        // SONG cover — centered hero
        if (heroCover && heroCover.complete && heroCover.naturalWidth > 0) {
          const size = 540;
          const x = (720 - size) / 2;
          const y = 200;
          ctx.save();
          ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = 40; ctx.shadowOffsetY = 12;
          // rounded rect clip
          const r = 28;
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.arcTo(x + size, y, x + size, y + size, r);
          ctx.arcTo(x + size, y + size, x, y + size, r);
          ctx.arcTo(x, y + size, x, y, r);
          ctx.arcTo(x, y, x + size, y, r);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(heroCover, x, y, size, size);
          ctx.restore();
        }

        // STATION badge — small bottom-right corner overlay on the hero,
        // ALWAYS rendered (even if trackCover is the hero) so listeners see
        // the broadcaster's brand in every clip.
        if (stationBadge && stationBadge.complete && stationBadge.naturalWidth > 0) {
          const heroX = (720 - 540) / 2;
          const heroY = 200;
          const heroSize = 540;
          const bSize = 96;
          const bx = heroX + heroSize - bSize - 18;
          const by = heroY + heroSize - bSize - 18;
          ctx.save();
          // subtle white ring + rounded clip
          ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = 16; ctx.shadowOffsetY = 4;
          const br = 14;
          ctx.beginPath();
          ctx.moveTo(bx + br, by);
          ctx.arcTo(bx + bSize, by, bx + bSize, by + bSize, br);
          ctx.arcTo(bx + bSize, by + bSize, bx, by + bSize, br);
          ctx.arcTo(bx, by + bSize, bx, by, br);
          ctx.arcTo(bx, by, bx + bSize, by, br);
          ctx.closePath();
          ctx.fillStyle = "white";
          ctx.fill();
          ctx.clip();
          ctx.drawImage(stationBadge, bx + 3, by + 3, bSize - 6, bSize - 6);
          ctx.restore();
          // "STATION" microlabel under the badge
          ctx.fillStyle = "rgba(255,255,255,0.7)";
          ctx.font = "bold 11px monospace";
          ctx.textAlign = "center";
          ctx.fillText("STATION", bx + bSize / 2, by + bSize + 18);
          ctx.textAlign = "left";
        }

        // Station name
        ctx.fillStyle = "white";
        ctx.font = "bold 52px Inter, system-ui, sans-serif";
        ctx.textAlign = "center";
        wrapText(ctx, station.name || "", 360, 830, 640, 56);
        ctx.fillStyle = "#F59E0B";
        ctx.font = "bold 22px monospace";
        ctx.fillText("// " + (station.tagline || "ON AIR · WORLDWIDE").slice(0, 60).toUpperCase(), 360, 900);

        // Now playing block
        if (title) {
          ctx.fillStyle = "#CBD5E1";
          ctx.font = "20px Inter, system-ui, sans-serif";
          ctx.fillText(`${title}${artist ? " · " + artist : ""}`.slice(0, 60), 360, 960);
        }
        ctx.textAlign = "left";

        // Animated waveform bars at the bottom
        analyser.getByteFrequencyData(freqData);
        const barCount = 32;
        const barW = 14;
        const gap = 6;
        const totalW = barCount * (barW + gap) - gap;
        const startX = (720 - totalW) / 2;
        const baseY = 1180;
        for (let i = 0; i < barCount; i++) {
          const v = freqData[Math.floor(i * freqData.length / barCount)] || 0;
          const h = Math.max(6, (v / 255) * 120);
          const x = startX + i * (barW + gap);
          ctx.fillStyle = i % 2 === 0 ? "#F59E0B" : "#FBBF24";
          ctx.fillRect(x, baseY - h, barW, h);
        }

        // fliboe.com watermark
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.font = "16px monospace";
        ctx.textAlign = "center";
        ctx.fillText("fliboe.com", 360, 1240);
        ctx.textAlign = "left";

        if (elapsed >= clipSeconds) {
          recorder.stop();
          try { audio.pause(); } catch (_) {}
          return;
        }
        requestAnimationFrame(drawFrame);
      };

      recorder.start();
      audio.currentTime = 0;
      await audio.play();
      requestAnimationFrame(drawFrame);
    } catch (e) {
      console.error(e);
      setErrMsg(e?.message || "Couldn't record the clip in this browser. Try Chrome or Edge on desktop.");
      setStatus("error");
    }
  };

  const download = () => {
    if (!downloadUrl) return;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${(station.name || "fliboe").replace(/[^a-z0-9-_]+/gi, "-")}-clip.mp4`;
    a.click();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="clip-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0a0a0a] border border-emerald-500/30 rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-emerald-500" />
            <span className="fl-display font-bold">Shareable clip</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="clip-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Primary CTA up top — most visible on mobile */}
          {status === "idle" && (
            <button
              onClick={start}
              disabled={!audioUrl}
              className="fl-btn w-full !py-3 text-sm"
              data-testid="clip-start"
            >
              <Sparkles className="w-4 h-4" /> Make {clipSeconds}-sec clip
            </button>
          )}
          {status === "recording" && (
            <div className="space-y-2" data-testid="clip-progress">
              <div className="flex items-center justify-between text-xs text-slate-300">
                <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" /> Recording — keep this tab in front…</span>
                <span className="fl-mono text-emerald-500">{Math.round(progress * 100)}%</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
          )}
          {status === "done" && (
            <button onClick={download} className="fl-btn w-full !py-3 text-sm" data-testid="clip-download">
              <Download className="w-4 h-4" /> Download your clip
            </button>
          )}

          <p className="text-sm text-slate-300">
            Records a {clipSeconds}-second 9:16 video for TikTok / Reels / Stories — branded
            with your station, cover art, and a live waveform. Rendered entirely on your device.
          </p>

          {/* Preview canvas: small thumbnail when idle, full size when recording */}
          <div className={`rounded-lg overflow-hidden bg-black border border-white/5 mx-auto transition-all ${status === "idle" ? "w-32" : "w-full"}`}>
            <canvas ref={canvasRef} style={{ width: "100%", aspectRatio: "9/16", display: "block" }} data-testid="clip-canvas" />
          </div>
          <audio ref={audioElRef} style={{ display: "none" }} />

          {errMsg && <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="clip-error">{errMsg}</div>}
        </div>
      </div>
    </div>
  );
}

// Helper: wrap long station names into 1-2 lines
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return;
  const words = String(text).split(" ");
  let line = "";
  let yy = y;
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + " " + words[i] : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = words[i];
      yy += lineHeight;
      if (yy > y + lineHeight * 2) { line = "…"; break; }
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
